import { Select as MantineSelect, SelectProps, SelectItem, Text } from "@mantine/core"
import { UseFormReturnType } from "@mantine/form/lib/use-form"
import merge from "lodash.merge"
import { forwardRef, useContext } from "react"
import {
  getQualificationColor,
  getFieldQualification,
  QualificationField,
  qualificationFields,
} from "~/prequal/utils"
import { EditingContext } from "../editingContext"
import { KeysOfType } from "~/utils/types"
import { ReadOnlyInput } from "./ReadOnlyInput"

const SelectWithDescription = forwardRef<
  HTMLDivElement,
  { value: string; label: string; description?: string }
>(({ label, description, ...props }, ref) => (
  <div ref={ref} {...props}>
    <Text size="sm">{label}</Text>
    <Text size="xs" color="dimmed">
      {description}
    </Text>
  </div>
))
SelectWithDescription.displayName = "SelectWithDescription"

type SelectInputProps<
  T extends Partial<Record<K, string | null>>,
  K extends KeysOfType<T, string | null>
> = Omit<SelectProps, "form" | "value" | "error" | "data" | "itemComponent"> & {
  form: UseFormReturnType<T>
  field: K
  data: SelectItem[]
}

export const SelectInput = <
  T extends Partial<Record<K, string | null>>,
  K extends KeysOfType<T, string | null>
>({
  form,
  field,
  styles,
  onChange,
  onBlur,
  ...props
}: SelectInputProps<T, K>) => {
  const editing = useContext(EditingContext)

  const qualificationField = qualificationFields.includes(field as QualificationField)
    ? (field as QualificationField)
    : false

  return editing ? (
    <MantineSelect
      {...form.getInputProps(field)}
      onChange={(value) => {
        form.setFieldValue(field, value as T[K])
        form.clearFieldError(field)
        onChange && onChange(value)
      }}
      onBlur={(event) => {
        form.validateField(field)
        onBlur && onBlur(event)
      }}
      itemComponent={SelectWithDescription}
      {...props}
      styles={(theme) => {
        const propStyles = typeof styles === "function" ? styles(theme) : styles
        return merge({}, propStyles, {
          input: {
            color: form.values[field]
              ? theme.colorScheme === "dark"
                ? theme.colors.dark[0]
                : theme.black
              : theme.colorScheme === "dark"
              ? theme.colors.dark[3]
              : theme.colors.gray[5],
          },
        })
      }}
    />
  ) : (
    <ReadOnlyInput
      value={props.data.find((option) => option.value === form.values[field])?.label}
      color={
        qualificationField
          ? getQualificationColor(getFieldQualification(qualificationField, form.values[field]))
          : undefined
      }
      error={field in form.errors}
      {...props}
    />
  )
}
